import { Checkbox, cn } from "@tellescope/ui";
import { Tooltip, Select } from "@tellescope/design-system";
import { OrgPlanName } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { getFeatureDisplayName } from "../../../../permissions";
import {
  ACCESS_ACTIONS,
  ACCESS_SELECT_COLORS,
  ACCESS_SELECT_VALUES,
  DataAccessFeature,
  getFeatureAccess,
  getFeatureSidebar,
  parseAccessLevel,
  toAccessSelectValue,
} from "./roleEditorConfig";
import { RoleEditor } from "./useEditRoleDialog";
import { useRoleEditorLabels } from "./useRoleEditorLabels";

export interface DataAccessRowProps {
  feature: DataAccessFeature;
  editor: RoleEditor;
  plan: OrgPlanName | undefined;
}

export const DataAccessRow = ({ feature, editor, plan }: DataAccessRowProps) => {
  const { t } = useTranslation("settings");
  const labels = useRoleEditorLabels();
  const { draft, setFeatureAccess, setFeatureSidebar } = editor;
  const featureLabel =
    (feature.feature ? getFeatureDisplayName({ plan, featureName: feature.feature }) : undefined) ??
    labels.features[feature.key] ??
    feature.key;

  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pl-4 pr-2 text-sm">
        <Tooltip>
          <Tooltip.Trigger asChild>
            <span className="block max-w-[120px] truncate">{featureLabel}</span>
          </Tooltip.Trigger>
          <Tooltip.Content>{featureLabel}</Tooltip.Content>
        </Tooltip>
      </td>
      <td className="py-2 px-2 text-center">
        {!feature.sidebarLocked && (
          <Checkbox
            checked={getFeatureSidebar(draft.permissions, feature)}
            onCheckedChange={(checked) => setFeatureSidebar(feature, checked === true)}
            aria-label={t("Show in Sidebar")}
          />
        )}
      </td>
      {ACCESS_ACTIONS.map((action) => {
        const selectValue = toAccessSelectValue(getFeatureAccess(draft.permissions, feature, action));
        return (
          <td key={action} className="py-2 px-2">
            <Select
              value={selectValue}
              onValueChange={(value) => setFeatureAccess(feature, action, parseAccessLevel(value))}
              disabled={feature.updateOnly && action !== "update"}
            >
              <Select.Trigger className={cn("h-8 w-32 border-0 text-xs", ACCESS_SELECT_COLORS[selectValue])}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                {ACCESS_SELECT_VALUES.map((value) => (
                  <Select.Item key={value} value={value} className="text-xs">
                    {labels.accessLevels[value]}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </td>
        );
      })}
    </tr>
  );
};
